import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, Clock, MapPin, Lightbulb, Volume2, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useSentencesData } from '@/hooks/useSentencesData';

const SentenceOfTheDay = memo(() => {
  const { sentences, sentenceOfTheDay, loading } = useSentencesData();
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);

  if (loading) {
    return (
      <section className="container mx-auto px-4 py-6">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-48 mx-auto mb-4"></div>
          <div className="bg-muted rounded-3xl h-[480px] max-w-3xl mx-auto"></div>
        </div>
      </section>
    );
  }

  const displaySentence = sentenceOfTheDay?.sentences_of_the_day || sentences[0];
  if (!displaySentence) return null;

  const handleClick = () => navigate('/sentences-of-the-day');

  const playPronunciation = () => {
    if ('speechSynthesis' in window) {
      setIsPlaying(true);
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(displaySentence.sentence);
      u.rate = 0.7;
      u.pitch = 1.15;
      u.onend = () => setIsPlaying(false);
      u.onerror = () => setIsPlaying(false);
      window.speechSynthesis.speak(u);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    "name": `Sentence of the Day: ${displaySentence.sentence}`,
    "description": `Learn English with: ${displaySentence.sentence}`,
    "educationalLevel": displaySentence.difficulty_level,
    "learningResourceType": "Grammar",
    "inLanguage": "en",
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <section className="container mx-auto px-4 py-4 relative">
        {/* Title */}
        <div className="text-center mb-3 relative z-10">
          <div className="inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-teal-500 animate-pulse" />
            <h2 className="text-xl md:text-2xl font-fredoka font-black bg-gradient-to-r from-teal-600 to-cyan-500 bg-clip-text text-transparent">
              Sentence of the Day! 💬
            </h2>
            <Sparkles className="w-5 h-5 text-cyan-500 animate-pulse" />
          </div>
        </div>

        <Card
          onClick={handleClick}
          className="max-w-3xl mx-auto cursor-pointer rounded-[2rem] border-4 border-white shadow-2xl overflow-hidden relative bg-gradient-to-br from-teal-100 via-cyan-200 to-teal-200 hover:scale-[1.01] transition-transform duration-300"
        >
          {/* Decorative blobs - mono teal */}
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-cyan-300/50 rounded-full blur-2xl pointer-events-none animate-pulse" />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-teal-300/50 rounded-full blur-2xl pointer-events-none animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-3 right-5 text-2xl pointer-events-none animate-bounce" style={{ animationDuration: '2.5s' }}>🌈</div>
          <div className="absolute bottom-3 left-5 text-2xl pointer-events-none animate-bounce" style={{ animationDuration: '3s', animationDelay: '0.5s' }}>🦋</div>

          <CardContent className="p-5 md:p-6 relative z-10">
            {/* Top: Big sentence centered */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-white/90 shadow-sm">
                <MessageCircle className="w-3.5 h-3.5 text-teal-600" />
                <span className="text-[11px] font-fredoka font-bold text-teal-700 uppercase tracking-wider">Today's Sentence</span>
              </div>

              <h3 className="text-2xl md:text-3xl font-fredoka font-black bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-700 bg-clip-text text-transparent leading-tight mb-2 animate-fade-in px-2">
                "{displaySentence.sentence}"
              </h3>

              <div className="flex items-center justify-center gap-1.5 mb-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-[11px] font-fredoka font-bold shadow-md">
                  🎯 {displaySentence.difficulty_level}
                </span>
                {displaySentence.category && (
                  <span className="px-2.5 py-0.5 rounded-full bg-white text-teal-700 text-[11px] font-fredoka font-bold border-2 border-teal-200 shadow-sm">
                    📚 {displaySentence.category}
                  </span>
                )}
              </div>
            </div>

            {/* Info cards - 2 column grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-3">
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-3 border-2 border-teal-200 shadow-sm">
                <p className="text-[11px] font-fredoka font-bold text-teal-800 mb-1 flex items-center gap-1">
                  <MapPin size={12} /> WHERE TO USE
                </p>
                <p className="text-sm font-fredoka text-gray-800 leading-snug line-clamp-3">
                  {displaySentence.usage_context}
                </p>
              </div>

              {displaySentence.time_context && (
                <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl p-3 border-2 border-cyan-200 shadow-sm">
                  <p className="text-[11px] font-fredoka font-bold text-cyan-800 mb-1 flex items-center gap-1">
                    <Clock size={12} /> WHEN TO USE
                  </p>
                  <p className="text-sm font-fredoka text-gray-800 leading-snug line-clamp-3">
                    {displaySentence.time_context}
                  </p>
                </div>
              )}
            </div>

            {displaySentence.example_situation && (
              <div className="bg-gradient-to-br from-cyan-100 to-teal-100 rounded-2xl p-3 border-2 border-cyan-200 shadow-sm mb-3">
                <p className="text-[11px] font-fredoka font-bold text-teal-800 mb-1 flex items-center gap-1">
                  <Lightbulb size={12} /> EXAMPLE
                </p>
                <p className="text-sm font-fredoka italic text-teal-900 leading-snug line-clamp-2">
                  {displaySentence.example_situation}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={(e) => { e.stopPropagation(); playPronunciation(); }}
                disabled={isPlaying}
                className="flex-1 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:opacity-90 text-white font-fredoka font-bold text-sm py-4 shadow-lg border-2 border-white"
              >
                <Volume2 className={`mr-2 h-4 w-4 ${isPlaying ? 'animate-pulse' : ''}`} />
                {isPlaying ? 'Listening…' : 'Say It!'}
              </Button>
              <Button
                onClick={handleClick}
                className="flex-1 rounded-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:opacity-90 text-white font-fredoka font-bold text-sm py-4 shadow-lg border-2 border-white"
              >
                Practice More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </>
  );
});

SentenceOfTheDay.displayName = 'SentenceOfTheDay';

export default SentenceOfTheDay;
